import React, { useState, useEffect } from 'react';
import { Search, Bell, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useRole, ViewRole } from '../../contexts/RoleContext';
import { usersApi, UserBrief } from '../../api/users';

const VIEW_AS_ROLES: { role: ViewRole; label: string; color: string }[] = [
  { role: 'technician',  label: 'Tech',    color: '#3b82f6' },
  { role: 'installer',   label: 'Install', color: '#0891b2' },
  { role: 'salesperson', label: 'Sales',   color: '#22c55e' },
  { role: 'office',      label: 'Office',  color: '#f97316' },
];

function ViewAsSwitcher() {
  const { user } = useAuth();
  const { activeRole, activePerson, isOverride, setActiveRole, setActivePerson, resetRole } = useRole();
  const [allUsers, setAllUsers] = useState<UserBrief[]>([]);

  useEffect(() => {
    usersApi.list().then(setAllUsers).catch(() => {});
  }, []);

  if (user?.role !== 'developer') return null;

  const activeConfig = VIEW_AS_ROLES.find(r => r.role === activeRole);
  const peopleForRole = activeConfig
    ? allUsers.filter(u => u.role === activeRole && u.is_active)
    : [];

  function handleRolePill(role: ViewRole) {
    if (activeRole === role && !activePerson) { resetRole(); return; }
    setActiveRole(role);
  }

  const selectStyle: React.CSSProperties = {
    height: '30px', borderRadius: '7px', fontSize: '12px', fontWeight: 500,
    border: '1px solid #e5e5e5', background: '#FAFAF8', color: '#111',
    padding: '0 8px', cursor: 'pointer', outline: 'none',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

      {/* Role dropdown */}
      <select
        value={activeConfig ? activeRole : ''}
        onChange={e => {
          const val = e.target.value as ViewRole;
          if (!val) resetRole();
          else handleRolePill(val);
        }}
        style={{
          ...selectStyle,
          borderColor: activeConfig ? activeConfig.color : '#e5e5e5',
          color: activeConfig ? activeConfig.color : '#a3a3a3',
          background: activeConfig ? `${activeConfig.color}10` : '#FAFAF8',
        }}
      >
        <option value="">View as…</option>
        {VIEW_AS_ROLES.map(({ role, label }) => (
          <option key={role} value={role}>{label}</option>
        ))}
      </select>

      {/* Person dropdown — shown once a role is selected */}
      {activeConfig && peopleForRole.length > 0 && (
        <select
          value={activePerson?.id ?? ''}
          onChange={e => {
            const id = Number(e.target.value);
            if (!id) { setActivePerson(null); return; }
            const u = allUsers.find(x => x.id === id);
            if (u) setActivePerson({ id: u.id, name: u.name, role: u.role, color: u.color });
          }}
          style={{
            ...selectStyle,
            borderColor: activePerson ? (activePerson.color ?? activeConfig.color) : '#e5e5e5',
            color: activePerson ? (activePerson.color ?? activeConfig.color) : '#737373',
            background: activePerson ? `${activePerson.color ?? activeConfig.color}10` : '#FAFAF8',
          }}
        >
          <option value="">Any {activeConfig.label}</option>
          {peopleForRole.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
      )}

      {/* Reset */}
      {isOverride && (
        <button
          onClick={resetRole}
          style={{
            height: '30px', padding: '0 10px', borderRadius: '7px', fontSize: '12px', fontWeight: 600,
            border: '1px solid #fecaca', background: '#fef2f2', color: '#ef4444', cursor: 'pointer',
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}

interface TopbarProps {
  toggleSidebar: () => void;
}

const Topbar: React.FC<TopbarProps> = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  return (
    <header
      style={{
        height: '56px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        background: '#fff',
        borderBottom: '1px solid #f0f0f0',
        flexShrink: 0,
      }}
    >
      {/* Left */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Hamburger — mobile only, toggled via JS below */}
        <button
          id="sidebar-toggle-btn"
          onClick={toggleSidebar}
          style={{
            display: 'none', // overridden by inline style on small screens via resize observer
            padding: '6px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: '#737373',
            borderRadius: '6px',
          }}
          aria-label="Toggle menu"
        >
          <Menu size={20} />
        </button>

        {/* Search */}
        <div style={{ position: 'relative' }}>
          <Search
            size={14}
            style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#a3a3a3', pointerEvents: 'none' }}
          />
          <input
            type="search"
            aria-label="Search"
            value={query}
            placeholder="Search…"
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && query.trim()) {
                navigate('/customers');
                setQuery('');
              }
            }}
            style={{
              height: '32px',
              paddingLeft: '30px',
              paddingRight: query ? '28px' : '12px',
              width: '220px',
              background: '#f5f5f5',
              border: '1px solid transparent',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#171717',
              outline: 'none',
            }}
            onFocus={e => { e.target.style.background = '#fff'; e.target.style.borderColor = '#d4d4d4'; }}
            onBlur={e => { e.target.style.background = '#f5f5f5'; e.target.style.borderColor = 'transparent'; }}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: '#a3a3a3', padding: 0, display: 'flex' }}
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Center — View As switcher (developer only) */}
      <ViewAsSwitcher />

      {/* Right */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <button
          style={{ padding: '6px', border: 'none', background: 'none', cursor: 'pointer', color: '#737373', borderRadius: '6px', display: 'flex' }}
          aria-label="Notifications"
        >
          <Bell size={17} />
        </button>

      </div>
    </header>
  );
};

export default Topbar;
